import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import Loading from "./Loading";
import { auth } from "../helper/firebaseClient";
import useAuth from "../stores/authStore";
import SubscriptionExpiredNotice from "./SubscriptionExpiredNotice";

// How old (ms) a server-fetched profile may be before we refuse to use it to
// send an org_admin to /checkout. Cached data from a previous session (or from
// a mobile/iPad tab resumed from memory) is never trusted for that decision.
const CHECKOUT_DECISION_MAX_AGE_MS = 15 * 1000;

// True when this profile says an org_admin must (re)pay.
export function orgAdminNeedsCheckout(profileData) {
  if (!profileData || profileData.role_priv !== "org_admin") return false;
  const isApproved =
    profileData?.flags?.is_approved ?? profileData?.is_approved ?? false;
  const subscriptionActive = profileData.subscription_active ?? true;
  return (
    profileData.status === "pending_payment" ||
    isApproved !== true ||
    !subscriptionActive
  );
}

// True when this profile must NOT reach the app: an org_admin who has to pay
// (-> /checkout), or any other member of an org whose subscription expired
// (-> blocking notice). Also used to hide the sidebar / top bar.
export function subscriptionBlocksAccess(profileData) {
  if (!profileData) return false;
  if (profileData.role_priv === "org_admin") {
    return orgAdminNeedsCheckout(profileData);
  }
  return profileData.subscription_active === false;
}

/**
 * ProtectedRoute enforces auth + state-based access control.
 *
 * Canonical user state:
 * - status: pending_payment | invited | onboarded | suspended
 * - flags.is_authenticated: True only after email verification + login
 * - flags.is_approved:
 *    - org_admin: True automatically after successful payment
 *    - member: True after org_admin approval
 */
export function ProtectedRoute() {
  const location = useLocation();
  const [authChecked, setAuthChecked] = useState(false);

  const { profileData, profileIsPending, setProfileIsPending } = useAuth();
  const profileFetchedAt = useAuth((state) => state.profileFetchedAt);
  const fetchProfileData = useAuth((state) => state.fetchProfileData);

  // Location key we already re-validated for (so a failed fetch can't loop).
  const [revalidatedKey, setRevalidatedKey] = useState(null);
  const [revalidating, setRevalidating] = useState(false);

  // Before redirecting an org_admin to /checkout, make sure that decision is
  // based on FRESH server data, not on profileData hydrated from
  // secureLocalStorage. That cache is per-device: if the customer renewed from
  // their laptop, the iPad/phone still has "subscription_active: false" cached
  // and would bounce them to /checkout until they cleared site data.
  const shouldRevalidate =
    authChecked &&
    !!auth.currentUser &&
    !!profileData &&
    !profileIsPending &&
    location.pathname !== "/checkout" &&
    subscriptionBlocksAccess(profileData) &&
    Date.now() - (profileFetchedAt || 0) > CHECKOUT_DECISION_MAX_AGE_MS &&
    revalidatedKey !== location.key;

  useEffect(() => {
    if (!shouldRevalidate) return;
    const key = location.key;
    setRevalidating(true);
    fetchProfileData({ silent: true }).finally(() => {
      setRevalidatedKey(key);
      setRevalidating(false);
    });
  }, [shouldRevalidate, location.key, fetchProfileData]);

  // Ensure Firebase auth state is known before evaluating route guards
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setAuthChecked(true);
      if (!user) {
        setProfileIsPending(false);
      }
    });

    return () => unsubscribe();
  }, [setProfileIsPending]);

  if (!authChecked) {
    return <Loading />;
  }

  if (auth.currentUser && !profileData) {
    return <Loading />;
  }

  if (profileIsPending || shouldRevalidate || revalidating) {
    return <Loading />;
  }

  // const flags = profileData?.flags || {};

  // Not authenticated
  const isAuthed =
    profileData?.is_authenticated ??
    profileData?.flags?.is_authenticated ??
    false;

  const isApproved =
    profileData?.flags?.is_approved ?? profileData?.is_approved ?? false;

  console.log("AUTH DEBUG", {
    firebaseUser: auth.currentUser,
    profileData,
    profileIsPending,
    authChecked,
  });

  if (!profileData || !isAuthed) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Suspended is always blocked
  if (profileData.status === "suspended") {
    return <Navigate to="/login" replace />;
  }

  // ---- EXPIRED SUBSCRIPTION, NOT THE ORG ADMIN ----
  // Members cannot pay, so they get a blocking notice (only reached with
  // server-verified data — see shouldRevalidate above).
  if (
    profileData.role_priv !== "org_admin" &&
    profileData.subscription_active === false
  ) {
    return <SubscriptionExpiredNotice />;
  }

  // ---- ORG ADMIN ----
  if (profileData.role_priv === "org_admin") {
    // pending payment, not approved, OR subscription has expired -> checkout
    // (only reached with server-verified data — see shouldRevalidate above)
    if (orgAdminNeedsCheckout(profileData)) {
      // allow them to be on checkout route
      if (location.pathname === "/checkout") return <Outlet />;
      return <Navigate to="/checkout" replace />;
    }
    return <Outlet />;
  }

  // ---- MEMBER ----
  if (profileData.status === "invited") {
    if (location.pathname === "/onboarding") return <Outlet />;
    return <Navigate to="/onboarding" replace />;
  }

  if (profileData.status === "onboarded") {
    // NOT APPROVED => must stay in waiting lobby
    if (!isApproved) {
      if (location.pathname === "/waiting-lobby") return <Outlet />;
      return <Navigate to="/waiting-lobby" replace />;
    }

    // APPROVED => allow dashboard (and block waiting lobby)
    if (location.pathname === "/waiting-lobby") {
      return <Navigate to="/dashboard" replace />;
    }

    return <Outlet />;
  }

  // Fallback (unknown state)
  return <Navigate to="/login" replace />;
}
